/**
 * AnimatedBubblesButton
 * 
 * Animated icon button with TikTok/YouTube-style animation effects when liked.
 * Mimics Flutter's LikeButton package with bubbles and circle animations.
 * Can be used for hearts, thumbs up, or any other icon.
 * 
 * Features:
 * - Icon scale animation on like (bounces with dramatic 0.2x shrink)
 * - Configurable particle bubbles that float outward with custom colors
 * - Expanding circle background effect with custom color
 * - Smooth color transitions
 * - No animation on unlike (just color change)
 * - Flexible bubble count based on array length
 * - Customizable icon via iconUrl prop
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface BubbleConfig {
    /** Color of the bubble */
    color: string;
}

export interface AnimatedBubblesButtonProps {
    /** Content/media id — used to make the per-card `data-qa-id` page-unique. */
    contentId: string;

    /** Whether the icon is currently liked */
    isLiked: boolean;

    /** Icon size in pixels */
    size: number;

    /** Primary color when liked */
    likeColor: string;

    /** Color when not liked */
    unlikeColor: string;

    /** Callback when icon is tapped */
    onTap: () => void;

    /** Animation duration in milliseconds */
    animationDuration?: number;

    /** Color for the expanding circle animation */
    circleColor?: string;

    /** Array of bubble configurations (colors). Length determines number of bubbles */
    bubbles?: BubbleConfig[];

    /** URL to the icon image (e.g., heart_fill.png, thumb_up.png) */
    iconUrl?: string;
}

interface Particle {
    id: number;
    angle: number;
    delay: number;
    scale: number;
    color: string;
}

export const AnimatedBubblesButton: React.FC<AnimatedBubblesButtonProps> = ({
    contentId,
    isLiked,
    size,
    likeColor,
    unlikeColor,
    onTap,
    animationDuration = 500,
    circleColor,
    bubbles,
    iconUrl = '/assets/images/heart_fill.png'
}) => {
    const { theme } = useTheme();
    const [isAnimating, setIsAnimating] = useState(false);
    const [particles, setParticles] = useState<Particle[]>([]);
    const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Default bubbles if not provided (6 bubbles alternating between two colors)
    const defaultBubbles: BubbleConfig[] = [
        { color: theme.accent },
        { color: theme.activeRed },
        { color: theme.accent },
        { color: theme.activeRed },
        { color: theme.accent },
        { color: theme.activeRed },
    ];

    const bubbleConfigs = bubbles && bubbles.length > 0 ? bubbles : defaultBubbles;
    const expandCircleColor = circleColor || likeColor;

    // Clean up animation timeout on unmount
    useEffect(() => {
        return () => {
            if (animationTimeoutRef.current) {
                clearTimeout(animationTimeoutRef.current);
            }
        };
    }, []);

    const triggerAnimation = () => {
        setIsAnimating(true);

        // Generate particles based on bubble configurations
        const newParticles: Particle[] = [];
        const bubbleCount = bubbleConfigs.length;
        for (let i = 0; i < bubbleCount; i++) {
            newParticles.push({
                id: Date.now() + i,
                angle: (360 / bubbleCount) * i,
                delay: Math.random() * 100,
                scale: 0.24, // Fixed size for all bubbles
                color: bubbleConfigs[i].color
            });
        }
        setParticles(newParticles);

        // Clear animation state after completion
        if (animationTimeoutRef.current) {
            clearTimeout(animationTimeoutRef.current);
        }
        animationTimeoutRef.current = setTimeout(() => {
            setIsAnimating(false);
            setParticles([]);
        }, animationDuration);
    };

    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        
        // Only animate when liking (not when unliking)
        if (!isLiked) {
            triggerAnimation();
        }
        
        onTap();
    };

    return (
        <div
            style={{
                position: 'relative',
                width: `${size}px`,
                height: `${size}px`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'visible' // Allow animations to expand beyond container
            }}
        >
            {/* Expanding circle background (only when animating) */}
            {isAnimating && (
                <div
                    style={{
                        position: 'absolute',
                        width: `${size * 1.2}px`,
                        height: `${size * 1.2}px`,
                        borderRadius: '50%',
                        // background: `radial-gradient(circle, ${expandCircleColor}CC, ${expandCircleColor}66, transparent)`,
                        animation: `expandCircle ${animationDuration}ms ease-out forwards`,
                        pointerEvents: 'none',
                        zIndex: 0,
                        boxShadow: `0 0 ${size * 0.25}px ${expandCircleColor}88`
                    }}
                />
            )}

            {/* Particle bubbles */}
            {isAnimating && (
                <>
                    {particles.map((particle) => {
                        const particleSize = size * particle.scale;

                        return (
                            <div
                                key={particle.id}
                                style={{
                                    position: 'absolute',
                                    width: `${particleSize}px`,
                                    height: `${particleSize}px`,
                                    borderRadius: '50%',
                                    backgroundColor: particle.color,
                                    animation: `floatParticle-${particle.id} ${animationDuration}ms ease-out forwards`,
                                    animationDelay: `${particle.delay}ms`,
                                    pointerEvents: 'none',
                                    opacity: 0,
                                    zIndex: 0,
                                    left: '50%',
                                    top: '50%',
                                    marginLeft: `-${particleSize / 2}px`,
                                    marginTop: `-${particleSize / 2}px`,
                                    willChange: 'transform, opacity'
                                }}
                            />
                        );
                    })}
                    
                    {/* Particle animations */}
                    <style>{`
                        ${particles.map((particle) => {
                            const angleRad = (particle.angle * Math.PI) / 180;
                            const distance = size * 2;
                            const endX = Math.cos(angleRad) * distance;
                            const endY = Math.sin(angleRad) * distance;
                            
                            return `
                                @keyframes floatParticle-${particle.id} {
                                    0% {
                                        transform: translate(0, 0) scale(0.3);
                                        opacity: 1;
                                    }
                                    50% {
                                        opacity: 1;
                                        transform: translate(${endX * 0.5}px, ${endY * 0.5}px) scale(1);
                                    }
                                    100% {
                                        transform: translate(${endX}px, ${endY}px) scale(0.8);
                                        opacity: 0;
                                    }
                                }
                            `;
                        }).join('\n')}
                    `}</style>
                </>
            )}

            {/* Animated icon button */}
            <button
                {...qaAttr({ id: `content-bubbles-${contentId}`, status: isLiked ? 'liked' : 'unliked' })}
                onClick={handleClick}
                className="focus:outline-none cursor-pointer"
                style={{
                    position: 'relative',
                    width: `${size}px`,
                    height: `${size}px`,
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    animation: isAnimating ? `iconBounce ${animationDuration}ms ease-out` : 'none',
                    zIndex: 1
                }}
            >
                <AssetIcon
                    path={iconUrl}
                    width={size}
                    height={size}
                    color={isLiked ? likeColor : unlikeColor}
                    transition={isAnimating ? 'none' : 'background-color 0.2s ease'}
                />
            </button>

            {/* CSS Keyframes */}
            <style>{`
        @keyframes iconBounce {
          0% {
            transform: scale(1);
          }
          10% {
            transform: scale(0.2);
          }
          30% {
            transform: scale(1.3);
          }
          50% {
            transform: scale(0.9);
          }
          70% {
            transform: scale(1.1);
          }
          85% {
            transform: scale(0.95);
          }
          100% {
            transform: scale(1);
          }
        }

        @keyframes expandCircle {
          0% {
            transform: scale(0.3);
            opacity: 0.9;
          }
          25% {
            opacity: 0.7;
          }
          50% {
            opacity: 0.5;
          }
          75% {
            opacity: 0.2;
          }
          100% {
            transform: scale(3.5);
            opacity: 0;
          }
        }
      `}</style>
        </div>
    );
};
